/**
 * FilmMismatchModal — persistent, explicit-dismiss film↔garment mismatch.
 *
 * PRODUCTION_DESIGN §6.1: the film↔garment MISMATCH is a station-BLOCKING modal
 * (never a toast). Image-first, expected-vs-scanned, red shake + buzz on open,
 * one blocking modal at a time, NO auto-dismiss — the operator must explicitly
 * acknowledge before the scanner re-arms.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertOctagon, Shirt } from 'lucide-react';
import { playErrorBeep } from '@/lib/sounds';
import { cn } from '@/lib/utils';
import { ZoomImg } from '@/components/production/HoverZoom';

interface FilmMismatchModalProps {
  open: boolean;
  /**
   * Increments on every mismatch EVENT (incl. back-to-back while the modal is
   * already open). Keys the DialogContent so it remounts → the shake animation
   * replays, and drives the per-event error buzz. Without this, a second wrong
   * scan while the modal is open would neither re-shake nor re-buzz.
   */
  mismatchSeq: number;
  expectedProductName: string;
  expectedImageUrl: string | null;
  onDismiss: () => void;
}

export function FilmMismatchModal({ open, mismatchSeq, expectedProductName, expectedImageUrl, onDismiss }: FilmMismatchModalProps) {
  const { t } = useTranslation('production');

  // Buzz on EVERY mismatch event (keyed on mismatchSeq), not just the open
  // transition — a wrong-item buzz is a critical cue and must fire each time
  // (PRODUCTION_DESIGN §6.1). playErrorBeep is stable.
  useEffect(() => {
    if (open) playErrorBeep();
  }, [open, mismatchSeq]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onDismiss(); }}>
      <DialogContent
        // Remount per mismatch event so animate-shake-error replays each time.
        key={mismatchSeq}
        className={cn(
          'sm:max-w-md border-2 border-destructive',
          // Reduced-motion: collapse the shake to the static destructive state
          // (red border is already present) — never remove the cue entirely
          // (PRODUCTION_DESIGN §6.2). motion-safe gates the animation.
          open && 'motion-safe:animate-shake-error motion-reduce:animate-none motion-reduce:bg-destructive/10',
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2.5 text-destructive">
            <AlertOctagon className="h-8 w-8 shrink-0" />
            {t('pressing.mismatchTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* Image-first: show the garment whose film was expected. */}
          <div className="flex items-center gap-4 rounded-lg border bg-muted/40 p-4">
            {expectedImageUrl ? (
              <ZoomImg src={expectedImageUrl} alt="" decoding="async" className="h-20 w-20 rounded object-cover shrink-0" />
            ) : (
              <div className="h-20 w-20 rounded bg-muted flex items-center justify-center shrink-0">
                <Shirt className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-lg font-semibold leading-tight">{t('pressing.mismatchExpected', { product: expectedProductName })}</p>
              <p className="text-sm text-destructive mt-1">{t('pressing.mismatchScanned')}</p>
            </div>
          </div>

          <p className="text-base">{t('pressing.mismatchBody')}</p>

          <Button
            className="w-full h-16 text-xl font-semibold"
            variant="destructive"
            onClick={onDismiss}
            autoFocus
          >
            {t('common:ok', 'OK')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
